<template>
  <div>
    <v-card
      v-if="loading || fetching"
      class="py-4 pl-8 pr-5"
      color="pwt_snow"
      elevation="3"
    >
      <v-row no-gutters>
        <v-col md="2" class="d-flex align-center">
          <div style="height: 70px; width: 100%; overflow: hidden">
            <v-skeleton-loader type="image" />
          </div>
        </v-col>
        <v-col md="10">
          <v-skeleton-loader type="paragraph" />
        </v-col>
      </v-row>
    </v-card>
    <div class="position-relative" v-else-if="vehicle">
      <v-card
        class="pa-0 position-relative"
        :style="{
          padding: '10px 30px 20px 25px !important',
          borderRadius: '10px',
          border: '1px solid rgb(var(--v-theme-primary))',
          background: 'rgb(var(--v-theme-primary20))',
          boxShadow: '0px 5px 13px 0px rgba(0, 0, 0, 0.07)',
        }"
        @click.stop="showVehicleDetails"
      >
        <v-row class="flex-column flex-md-row" no-gutters>
          <v-col md="2">
            <!-- image section -->
            <v-img
              contain
              :src="imageSrc(vehicle)"
              aspect-ratio="1"
              :width="mobile ? '40%' : '100%'"
              :height="mobile ? 'auto' : '100%'"
              style="margin: auto"
            ></v-img>
          </v-col>
          <v-col md="10">
            <!-- details section -->
            <v-card-title class="pb-0">
              {{
                vehicle.licensePlate ||
                vehicle.name ||
                "Current default vehicle"
              }}
            </v-card-title>
            <div
              v-if="overdueWarningText"
              class="d-flex align-center px-4 text-error"
            >
              <v-icon color="error" size="small" class="mr-1">
                mdi-alert-circle-outline
              </v-icon>
              {{ overdueWarningText }}
            </div>
            <div class="d-flex flex-wrap ga-4 px-4 mt-2">
              <div class="d-flex align-center">
                <v-icon color="primary" size="small" class="mr-1">
                  {{ fuelIcon }}
                </v-icon>
                {{ fuelType }}
              </div>
              <div
                v-if="capacityIcon && capacityText"
                class="d-flex align-center"
              >
                <v-icon color="primary" size="small" class="mr-1">
                  {{ capacityIcon }}
                </v-icon>
                {{ capacityText }}
              </div>
              <div
                v-if="showBootCapacity && bootCapacityText"
                class="d-flex align-center"
              >
                <v-icon color="primary" size="small" class="mr-1">
                  mdi-package-variant-closed
                </v-icon>
                {{ bootCapacityText }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </div>
    <v-card
      v-else
      elevation="3"
      rounded="lg"
      class="py-4 pl-8 pr-5 d-flex flex-column flex-md-row"
    >
      <div class="flex-grow-1 flex-shrink-1">
        <h5 class="text-h6">Select your default vehicle</h5>
        <p class="text-caption">
          You can compare different vehicles and their performance later on.
        </p>
      </div>
      <div class="d-flex justify-center align-center mt-2 mt-sm-0">
        <CustomOutlinedButton
          shape="box"
          @click="showVehicleGalleryDialog = true"
        >
          Add
        </CustomOutlinedButton>
      </div>
    </v-card>
    <GenericDialog
      :activator="showVehicleGalleryDialog"
      @close="showVehicleGalleryDialog = false"
    >
      <VehicleSearchContent
        @vehicle-select="handleVehicleSelect"
        show-generic
      />
    </GenericDialog>
  </div>
</template>
<script setup lang="ts">
import { useFleetStore } from "@/domains/fleet/fleetStore.ts";
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import { useUserStore } from "@/domains/user/userStore.ts";
import { useVehicleStore, type Vehicle } from "@/domains/vehicles";
import { useDashboardNavStore } from "@/stores/dashboardNavStore";
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import GenericDialog from "../dialog-wrappers/GenericDialog.vue";
import CustomOutlinedButton from "../ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import VehicleSearchContent from "./VehicleSearchContent.vue";

const vehicleStore = useVehicleStore();
const fleetStore = useFleetStore();
const userStore = useUserStore()
const inspectionHistoryStore = useInspectionHistoryStore();

const { mobile } = useDisplay();
const fetching = computed<boolean>(
  () => userStore.isFetching || vehicleStore.isFetching,
);
const vehicle = computed<Vehicle | undefined>(
  () => vehicleStore.defaultVehicle,
);
const showVehicleGalleryDialog = ref(false);
const {
  loading = false,
  showBootCapacity = false,
} = defineProps<{
  loading?: boolean;
  showBootCapacity?: boolean;
}>();

function imageSrc(vehicle: Vehicle): string {
  // get uploaded image to car in first instance.
  const images = vehicle.imageSrcPaths;
  if (images?.[0]) return images[0];
  // get default image in fourth instance.
  return "/images/car_images/placeholder_car.png";
}

async function handleVehicleSelect(vehicle: Vehicle): Promise<void> {
  // close dialog
  showVehicleGalleryDialog.value = false;
  // update the user's default vehicle preference
  await userStore.setAndUpdatePrefs({
    key: "selectedVehicleExternalId",
    value: vehicle.externalId,
  });
}

const dashboardNavStore = useDashboardNavStore();

function showVehicleDetails() {
  if (!vehicle.value) return;
  dashboardNavStore.showVehicleDetails(vehicle.value.id);
}

const fuelIcon = computed(() => vehicle.value?.fuelTypeIcon);

const fuelType = computed(() => vehicle.value?.fuelTypeDisplayText);

const capacityIcon = computed(() => vehicle.value?.fuelCapacityIcon);

const capacityText = computed(() => vehicle.value?.fuelCapacityDisplayText);

const bootCapacityText = computed(() =>
  vehicle.value?.externalId
    ? vehicleStore.getBootCapacityTextForVehicle(vehicle.value.externalId)
    : undefined,
);

/** Whole days from now until the given ISO date string (negative if past). */
function daysUntil(dateString: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((new Date(dateString).getTime() - Date.now()) / msPerDay);
}

/**
 * True if any of the vehicle's compliance status rows indicate an expired
 * WOF/COF — mirrors VehicleDetails.vue's wofCofExpired check.
 */
const wofCofExpired = computed(() => {
  if (!vehicle.value?.externalId) return false;

  return inspectionHistoryStore
    .getComplianceStatusForVehicle(vehicle.value.externalId)
    .some(
      (status) =>
        (status.days_until_due ?? 0) < 0 ||
        status.wof_cof_status?.toUpperCase().includes("EXPIRED"),
    );
});

const registrationExpired = computed(
  () =>
    !!vehicle.value?.registrationExpiry &&
    daysUntil(vehicle.value.registrationExpiry) < 0,
);

const overdueWarningText = computed(() => {
  if (wofCofExpired.value && registrationExpired.value) {
    return "WOF/COF and registration overdue";
  }
  if (wofCofExpired.value) return "WOF/COF overdue";
  if (registrationExpired.value) return "Registration overdue";
  return undefined;
});
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
  opacity: 0.6; /* Add this line to set a minimum opacity value */
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
